/**
 * Hook for filtering publications by search query
 * Searches in title, body, pubRef, pubId, and theme names
 */

'use client';

import { useMemo, useState } from 'react';
import type { PublicationTeaserType } from '../types/publication';

export interface UsePublicationSearchOptions {
  publications: PublicationTeaserType[];
}

/**
 * Hook for searching publications
 */
export function usePublicationSearch({ publications }: UsePublicationSearchOptions) {
  const [query, setQuery] = useState('');

  const normalizedQuery = query.trim().toLowerCase();

  // Filter publications based on search query
  const filteredPublications = useMemo(() => {
    if (!normalizedQuery) {
      return publications;
    }

    return publications.filter(pub => {
      // Search in title
      const title = pub.title?.toLowerCase() || '';
      if (title.includes(normalizedQuery)) {
        return true;
      }

      // Search in body value (description)
      const bodyValue = pub.body?.value?.toLowerCase() || '';
      if (bodyValue.includes(normalizedQuery)) {
        return true;
      }

      // Search in body summary
      const bodySummary = pub.body?.summary?.toLowerCase() || '';
      if (bodySummary.includes(normalizedQuery)) {
        return true;
      }

      // Search in pubRef
      const pubRef = pub.pubRef?.toLowerCase() || '';
      if (pubRef.includes(normalizedQuery)) {
        return true;
      }

      // Search in pubId
      const pubId = pub.pubId?.toLowerCase() || '';
      if (pubId.includes(normalizedQuery)) {
        return true;
      }

      // Search in theme names
      if (pub.theme?.some(theme => theme?.name?.toLowerCase().includes(normalizedQuery))) {
        return true;
      }

      return false;
    });
  }, [publications, normalizedQuery]);

  const totalResults = filteredPublications.length;
  const hasResults = totalResults > 0;
  const hasActiveSearch = normalizedQuery !== '';

  return {
    query,
    setQuery,
    filteredPublications,
    totalResults,
    hasResults,
    hasActiveSearch,
    normalizedQuery,
  };
}

